﻿import { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Chip,
    useMediaQuery,
    useTheme,
    Stack,
    Card,
    IconButton,
    CircularProgress,
} from '@mui/material';
import {
    FilterList as FilterIcon,
    CheckCircle as CheckCircleIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import {
    useGetResourcesSettingsQuery,
    useSaveResourcesSettingsMutation,
    useGetUserInfoQuery,
} from '@/store/apiSlice';
import { themeConstants, styleHelpers } from '@/theme';
import type { Category } from '@/types';
import CategoryFilterChip from '@/components/CategoryFilterChip';
import { useAppDispatch, useAppSelector, useCategories } from '@/store/hooks.ts';
import { close } from '@/store/uiSlice.ts';
import { useI18nContext } from '@/i18n/i18n-react';
import { useAppDialog } from '@/components/dialogs/AppDialog';
import { getApiErrorMessage } from '@/utils/apiError';

const FilterModal = () => {
    const { data: userInfo } = useGetUserInfoQuery();
    const categories = useCategories();

    const dispatch = useAppDispatch();
    const open = useAppSelector((state) => state.ui.open.filter);
    const onClose = () => dispatch(close('filter'));
    const { LL } = useI18nContext();
    const { alert } = useAppDialog();

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const { data: resourcesSettings } = useGetResourcesSettingsQuery();
    const [saveResourcesSettings, { isLoading: isSaving }] = useSaveResourcesSettingsMutation();

    const [localHiddenCategories, setLocalHiddenCategories] = useState<string[]>([]);

    useEffect(() => {
        if (open) {
            if (resourcesSettings) {
                setLocalHiddenCategories([...resourcesSettings.hiddenCategories]);
            }
        }
    }, [open, resourcesSettings]);

    if (!userInfo) {
        return null;
    }

    const userId = userInfo?.userId || 0;

    // Memoize groups computation
    const groups = useMemo(() => {
        const result: Record<string, Category[]> = {};
        categories.forEach((category) => {
            if (!result[category.group]) {
                result[category.group] = [];
            }
            result[category.group].push(category);
        });
        return result;
    }, [categories]);

    // Check if user is assigned to a category
    const isUserAssignedToCategory = (category: Category): boolean => {
        return category.resources?.some((resource) => resource.id === userId) || false;
    };

    // Toggle category in local state only (not saved yet)
    const toggleCategory = (categoryName: string) => {
        setLocalHiddenCategories((prev) => {
            if (prev.includes(categoryName)) {
                return prev.filter((name) => name !== categoryName);
            } else {
                return [...prev, categoryName];
            }
        });
    };

    // Toggle all categories in a group
    const toggleGroup = (groupCategories: Category[]) => {
        const groupCategoryNames = groupCategories
            .filter((cat) => !isUserAssignedToCategory(cat))
            .map((cat) => cat.name);

        // Check if all non-assigned categories in the group are visible
        const allVisible = groupCategoryNames.every(
            (name) => !localHiddenCategories.includes(name)
        );

        setLocalHiddenCategories((prev) => {
            if (allVisible) {
                // Hide all non-assigned categories in the group
                return [...prev, ...groupCategoryNames.filter((name) => !prev.includes(name))];
            } else {
                // Show all categories in the group
                return prev.filter((name) => !groupCategoryNames.includes(name));
            }
        });
    };

    // Save all changes when user clicks "Apply Filters"
    const handleApplyFilters = async () => {
        try {
            await saveResourcesSettings({ hiddenCategories: localHiddenCategories }).unwrap();
            onClose();
        } catch (error) {
            console.error('Failed to save filter settings:', error);
            await alert(getApiErrorMessage(error) || LL.errors.failedToSave());
        }
    };

    // Calculate counts
    const totalHidden = localHiddenCategories.length;
    const totalCategories = categories.length;
    const totalVisible = totalCategories - totalHidden;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen={isSmallScreen}
            slotProps={{
                paper: {
                    sx: {
                        minHeight: { xs: '100vh', sm: '60vh' },
                    },
                },
            }}
        >
            <Box sx={styleHelpers.dialogHeader(themeConstants.gradients.secondary)}>
                <Stack direction="row" sx={{ alignItems: 'center', gap: 2 }}>
                    <Box sx={styleHelpers.iconBox()}>
                        <FilterIcon sx={{ fontSize: 32, color: 'white' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
                            {LL.filterModal.title()}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                            {totalVisible > 0
                                ? LL.filterModal.subtitle({
                                      visible: totalVisible,
                                      total: totalCategories,
                                  })
                                : LL.filterModal.subtitleEmpty()}
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: 'white',
                            '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.1)',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </Box>

            <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
                {Object.entries(groups).map(([groupName, cats]) => (
                    <Card
                        key={groupName}
                        sx={{
                            mb: 2,
                            background: 'rgba(59,130,246,0.05)',
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Box sx={{ p: 2 }}>
                            <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mb: 2 }}>
                                <CheckCircleIcon sx={{ color: 'primary.main' }} />
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: '1rem', sm: '1.25rem' },
                                    }}
                                >
                                    {groupName}
                                </Typography>
                                <Chip
                                    label={
                                        cats.filter(
                                            (c) =>
                                                isUserAssignedToCategory(c) ||
                                                !localHiddenCategories.includes(c.name)
                                        ).length
                                    }
                                    size="small"
                                    variant="filled"
                                    sx={{
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                        fontWeight: 700,
                                    }}
                                />
                                <Box sx={{ flexGrow: 1 }} />
                                {(() => {
                                    const hasCats = cats
                                        .filter((c) => !isUserAssignedToCategory(c))
                                        .every((c) => !localHiddenCategories.includes(c.name));

                                    return (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => toggleGroup(cats)}
                                            sx={{
                                                fontSize: '0.75rem',
                                                py: 0.5,
                                                px: 1.5,
                                            }}
                                        >
                                            {hasCats
                                                ? LL.filterModal.hideAll()
                                                : LL.filterModal.showAll()}
                                        </Button>
                                    );
                                })()}
                            </Stack>
                            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1.5 }}>
                                {[...cats]
                                    .sort((a, b) => {
                                        const priorityA = a.sortPriority ?? 500;
                                        const priorityB = b.sortPriority ?? 500;

                                        if (priorityA !== priorityB) {
                                            return priorityA - priorityB;
                                        }

                                        return a.name.localeCompare(b.name);
                                    })
                                    .map((category) => {
                                        if (!category.active) {
                                            return (
                                                <CategoryFilterChip
                                                    key={category.id}
                                                    tooltip={LL.filterModal.inactiveCategoryTooltip(
                                                        { name: category.name }
                                                    )}
                                                    category={category}
                                                    isHidden={true}
                                                    isLocked={true}
                                                    borderColor="error.main"
                                                    borderWidth={2}
                                                    onToggle={() => {}}
                                                />
                                            );
                                        }

                                        if (isUserAssignedToCategory(category)) {
                                            return (
                                                <CategoryFilterChip
                                                    key={category.id}
                                                    tooltip={LL.filterModal.assignedCategoryTooltip(
                                                        { name: category.name }
                                                    )}
                                                    category={category}
                                                    isHidden={false}
                                                    isLocked={true}
                                                    borderColor="divider"
                                                    onToggle={() => {}}
                                                />
                                            );
                                        }

                                        return (
                                            <CategoryFilterChip
                                                key={category.id}
                                                category={category}
                                                isHidden={localHiddenCategories.includes(
                                                    category.name
                                                )}
                                                onToggle={() => toggleCategory(category.name)}
                                            />
                                        );
                                    })}
                            </Stack>
                        </Box>
                    </Card>
                ))}
            </DialogContent>
            <DialogActions sx={styleHelpers.dialogFooter()}>
                <Stack
                    direction="row"
                    gap={2}
                    sx={{ justifyContent: 'flex-end', flexGrow: 1, flexWrap: 'wrap' }}
                >
                    <Button
                        onClick={handleApplyFilters}
                        variant="contained"
                        color="secondary"
                        size="large"
                        fullWidth={isSmallScreen}
                        disabled={isSaving}
                        startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
                        sx={{ minWidth: { xs: '100%', sm: 200 } }}
                    >
                        {isSaving ? LL.common.loading() : LL.filterModal.applyFilters()}
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default FilterModal;
